import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { PeriodType } from '@/lib/api/generated/model';
import type { PeriodBounds } from '@/lib/api/generated/model';
import { thisWeekMondayKey, todayKey } from '../periodKeys';
import { monthLabel } from '../periodCopy';

export interface PeriodStepperProps {
  period: PeriodBounds;
  /** The earliest period with recorded usage; bounds how far back stepping goes. */
  earliestPeriodKey: string | null;
  onChange: (periodKey: string | undefined) => void;
}

/**
 * Move between periods (spec 072 FR-020).
 *
 * Bounded at both ends. Backwards stops at the earliest period the ledger holds, because
 * stepping into months that predate the workspace shows an empty page that looks like a
 * fault. Forwards stops at the current period, because a future month cannot have spend
 * and offering it invites the reader to wonder whether it should.
 *
 * Returning to the current period clears the parameter rather than naming it, so the
 * page's default address stays the one without a period in it — a link someone shares
 * "now" keeps meaning now.
 *
 * Steps in the unit being read: a day, a week or a month, labelled as such. It used to
 * step months from today's month whatever was on screen, and switch the page back to
 * Monthly, so a reader who had stepped to September and picked Daily saw "October" and
 * lost September. The lifetime total has nothing to step through, so no stepper.
 * Earliest is still a month (`earliest_period_key`): a day or week is reachable when
 * its month is.
 */
export function PeriodStepper({
  period,
  earliestPeriodKey,
  onChange,
}: PeriodStepperProps) {
  // Nothing to step through: the lifetime total is one period.
  if (period.period_type === PeriodType.none) return null;

  // Steps in the unit being read (a day, a week, a month), from the period on screen.
  // It used to step months from today whatever was shown, which read as the page
  // forgetting the month someone had stepped to.
  const steps = stepsFor(period);
  const previous = steps.previous;
  const next = steps.next;
  const atCurrent = period.is_current;
  const canGoBack =
    earliestPeriodKey !== null &&
    monthOf(steps.previousStart) >= earliestPeriodKey;

  return (
    <div className="flex items-center gap-1 max-md:w-full">
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Previous period, ${steps.previousLabel}`}
        disabled={!canGoBack}
        onClick={() => {
          onChange(previous);
        }}
      >
        <ChevronLeftIcon className="size-4" aria-hidden="true" />
      </Button>
      {/*
        `aria-live="polite"`: stepping replaces the page's figures without moving focus,
        so without this a screen-reader user gets new numbers and no announcement that
        the period changed underneath them.
      */}
      <span
        // On a phone the arrows go to the row's two ends (spec 277).
        className="min-w-32 text-center text-sm font-medium max-md:flex-1"
        aria-live="polite"
      >
        {steps.label}
      </span>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Next period, ${steps.nextLabel}`}
        disabled={atCurrent}
        onClick={() => {
          // Clearing rather than naming the current period — see the component's note.
          onChange(atCurrent ? undefined : next);
        }}
      >
        <ChevronRightIcon className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * Step a `YYYY-MM` key by whole months.
 *
 * Done on the string rather than through `Date`. A `Date` built from `2026-09` is UTC
 * midnight, and month arithmetic on it in a westward timezone lands on the previous
 * month — the same trap the daily labels avoid, and it would be invisible until someone
 * in a different zone reported that stepping skipped a month.
 */
const DAY = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const DAY_MS = 24 * 60 * 60 * 1000;

interface Steps {
  label: string;
  previous: string;
  previousLabel: string;
  previousStart: Date;
  next: string;
  nextLabel: string;
}

/** Where the arrows lead from this period, and how each is said. */
function stepsFor(period: PeriodBounds): Steps {
  const start = new Date(period.starts_at);
  if (period.period_type === PeriodType.daily) {
    const before = new Date(start.getTime() - DAY_MS);
    const after = new Date(start.getTime() + DAY_MS);
    return {
      label: DAY.format(start),
      previous: todayKey(before),
      previousLabel: DAY.format(before),
      previousStart: before,
      next: todayKey(after),
      nextLabel: DAY.format(after),
    };
  }
  if (period.period_type === PeriodType.weekly) {
    const before = new Date(start.getTime() - 7 * DAY_MS);
    const after = new Date(start.getTime() + 7 * DAY_MS);
    return {
      label: `Week of ${DAY.format(start)}`,
      previous: thisWeekMondayKey(before),
      previousLabel: `week of ${DAY.format(before)}`,
      previousStart: before,
      next: thisWeekMondayKey(after),
      nextLabel: `week of ${DAY.format(after)}`,
    };
  }
  const previous = shiftPeriod(period.key, -1);
  const next = shiftPeriod(period.key, 1);
  return {
    label: monthLabel(period.key),
    previous,
    previousLabel: monthLabel(previous),
    previousStart: new Date(`${previous}-01T00:00:00Z`),
    next,
    nextLabel: monthLabel(next),
  };
}

/** The `YYYY-MM` a date falls in, in UTC — the shape of `earliestPeriodKey`. */
function monthOf(date: Date): string {
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function shiftPeriod(key: string, months: number): string {
  const [year, month] = key.split('-').map(Number);
  const zeroBased = year * 12 + (month - 1) + months;
  return `${String(Math.floor(zeroBased / 12)).padStart(4, '0')}-${String((zeroBased % 12) + 1).padStart(2, '0')}`;
}
